/*
 * Product upsell cards.
 *
 * Tapping a card adds its variant to the cart and ticks it; tapping again
 * removes that line and clears the tick. Everything is scoped to the
 * <product-upsell> element, so several can sit on one page and a section
 * re-render in the theme editor re-initialises cleanly.
 *
 * Cart writes go through the theme's own path — the cart routes with the
 * drawer's sections, then the pub/sub events the rest of the theme listens
 * for — rather than patching counters and drawer markup by hand.
 */
if (!customElements.get('product-upsell')) {
  class ProductUpsell extends HTMLElement {
    constructor() {
      super();
      this.lines = new Map(); // variant id -> cart line key
      this.pending = new Set(); // variant ids with a request in flight
      this.lastFocused = null;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this.mountLayer();
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.syncFromCart();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.layer?.remove();
    }

    /*
     * The product details column is position: sticky with a z-index, which
     * makes it a stacking context. Anything rendered inside it — however high
     * its own z-index — is painted within that context, so the popup ended up
     * beneath the gallery image. No z-index can win that; the dialogs have to
     * leave the container. They are moved to a layer on <body>, where their
     * z-index counts against the page.
     */
    mountLayer() {
      const dialogs = [...this.querySelectorAll('.nb-popup, .nb-overlay')];
      if (!dialogs.length) return;

      this.layer = document.createElement('div');
      this.layer.className = 'nb-upsell-layer';
      dialogs.forEach((node) => this.layer.appendChild(node));

      document.body.appendChild(this.layer);
      this.layer.addEventListener('click', this.onClick);
    }

    /** Cards stay in place; dialogs live in the body layer. */
    get dialogs() {
      return this.layer || this;
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      const seeMore = event.target.closest('.nb-see-more');
      if (seeMore) {
        event.preventDefault();
        event.stopPropagation();
        this.openPopup(seeMore.dataset.popup);
        return;
      }

      if (event.target.closest('.nb-popup-close') || event.target.closest('.nb-overlay')) {
        this.closePopup();
        return;
      }

      const addButton = event.target.closest('.nb-popup-add-btn');
      if (addButton) {
        this.toggle(addButton.dataset.variantId);
        return;
      }

      const arrow = event.target.closest('.nb-carousel-btn');
      if (arrow) {
        this.scrollCarousel(arrow);
        return;
      }

      const card = event.target.closest('.nb-card');
      if (card) this.toggle(card.dataset.variantId);
    }

    // The cards are containers rather than buttons, so they need their own
    // key handling to stay operable from the keyboard.
    onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const card = event.target.closest('.nb-card');
      if (!card) return;

      event.preventDefault();
      this.toggle(card.dataset.variantId);
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.closePopup();
    }

    /* ------------------------------------------------------------- carousels */

    scrollCarousel(button) {
      const track = this.querySelector(`#${CSS.escape(button.dataset.carousel)} .nb-carousel-track`);
      if (!track) return;

      const step = (track.querySelector('.nb-card')?.offsetWidth || 220) + 8;
      const direction = button.classList.contains('nb-carousel-btn--prev') ? -1 : 1;

      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    /* ---------------------------------------------------------------- popups */

    openPopup(id) {
      if (!id) return;

      const popup = this.dialogs.querySelector(`#${CSS.escape(id)}`);
      if (!popup) return;

      this.closePopup();
      this.lastFocused = document.activeElement;

      popup.classList.add('nb-popup--open');
      this.dialogs.querySelector('.nb-overlay')?.classList.add('nb-overlay--open');
      document.body.style.overflow = 'hidden';

      popup.querySelector('.nb-popup-close')?.focus();
    }

    closePopup() {
      const open = this.dialogs.querySelector('.nb-popup--open');
      if (!open) return;

      open.classList.remove('nb-popup--open');
      this.dialogs.querySelector('.nb-overlay')?.classList.remove('nb-overlay--open');
      document.body.style.overflow = '';

      // Send focus back where it came from, not to the top of the document.
      this.lastFocused?.focus?.();
      this.lastFocused = null;
    }

    /* ------------------------------------------------------------------ cart */

    async toggle(variantId) {
      // Locking per variant rather than globally lets two different cards be
      // toggled at once while still ignoring a double tap on the same one.
      if (!variantId || this.pending.has(variantId)) return;

      this.pending.add(variantId);
      this.setBusy(variantId, true);

      try {
        const inCart = this.lines.has(variantId);
        const data = inCart ? await this.removeLine(variantId) : await this.addLine(variantId);

        if (inCart) {
          this.readLines(data);
        } else if (data.key) {
          this.lines.set(String(data.id), data.key);
        }

        this.render();
        this.announce(variantId, data);
      } catch (error) {
        this.publishError(variantId, error);
      } finally {
        this.pending.delete(variantId);
        this.setBusy(variantId, false);
      }
    }

    async addLine(variantId) {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);

      return this.post(window.routes?.cart_add_url || '/cart/add', body);
    }

    async removeLine(variantId) {
      const key = this.lines.get(variantId) || (await this.findLineKey(variantId));
      if (!key) return null;

      const body = new FormData();
      body.append('id', key);
      body.append('quantity', 0);

      return this.post(window.routes?.cart_change_url || '/cart/change', body);
    }

    async post(url, body) {
      const drawer = document.querySelector('cart-drawer, quick-cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.append('sections', drawer.getSectionsToRender().map((s) => s.section).join(','));
        body.append('sections_url', window.location.pathname);
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });

      const data = await response.json();
      if (data.status) throw data;

      return data;
    }

    async findLineKey(variantId) {
      const cart = await fetch('/cart.js').then((r) => r.json());
      return cart.items.find((item) => String(item.id) === String(variantId))?.key;
    }

    async syncFromCart() {
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        this.readLines(cart);
        this.render();
      } catch (error) {
        // A failed read just means nothing shows as added; leave the UI alone.
      }
    }

    readLines(cart) {
      if (!cart?.items) return;

      this.lines.clear();
      cart.items.forEach((item) => this.lines.set(String(item.id), item.key));
    }

    /* --------------------------------------------------------------- theme UI */

    // Hand off to the theme so the drawer and counters update the way they do
    // for any other add to cart.
    announce(variantId, data) {
      if (!data) return;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-upsell',
          productVariantId: variantId,
          cartData: data,
        });
      }

      const drawer = document.querySelector('cart-drawer, quick-cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
        drawer.renderContents(data);
      }

      if (typeof updateCartCounters === 'function') updateCartCounters();
    }

    publishError(variantId, error) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-upsell',
          productVariantId: variantId,
          errors: error?.errors || error?.description,
          message: error?.message,
        });
      }

      console.error('[product-upsell] cart update failed', error);
    }

    /* ---------------------------------------------------------------- render */

    render() {
      this.querySelectorAll('.nb-card').forEach((card) => {
        const active = this.lines.has(card.dataset.variantId);
        card.classList.toggle('nb-card--active', active);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.dialogs.querySelectorAll('.nb-popup-add-btn').forEach((button) => {
        const active = this.lines.has(button.dataset.variantId);
        button.classList.toggle('nb-popup-add-btn--active', active);
        button.textContent = active ? '✓ Added to Order' : 'Add to Order';
      });
    }

    setBusy(variantId, busy) {
      this.querySelectorAll(`.nb-card[data-variant-id="${CSS.escape(variantId)}"]`).forEach((card) => {
        card.classList.toggle('nb-card--loading', busy);
        card.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    }
  }

  customElements.define('product-upsell', ProductUpsell);
}
